/**
 * @file The wire shape of the assistant subscription history and the helpers
 * that construct it. Every other file in `useAssistantChat/` that produces,
 * stores, or asserts on history entries depends on this module.
 */

import { MessageSender } from '../../../generated/graphql'
import { ChatMessage, ChatMessageSender } from '../chatMessage'
import { ContentBlock } from '../domTools'

/**
 * Maximum number of history entries forwarded to the subscription. Bounds the
 * LLM context window — older turns are dropped client-side before the request
 * leaves the browser.
 */
export const MAX_HISTORY_MESSAGES = 20

/**
 * One entry in the conversation history transmitted to the subscription. Text
 * turns carry plain `content`; tool turns carry `toolContent` (a structured
 * block array consumed by the server to reconstruct LLM history).
 */
export type HistoryEntry = {
  sender: MessageSender
  content: string
  toolContent?: ContentBlock[]
}

/**
 * Builds the `[HistoryEntry, HistoryEntry]` pair for one tool round-trip: an
 * assistant turn with the tool_use call(s), then a user turn with the
 * matching tool_result(s).
 */
export const makeToolTurnPair = (
  assistant: ContentBlock[],
  user: ContentBlock[]
): [HistoryEntry, HistoryEntry] => [
  { sender: MessageSender.Assistant, content: '', toolContent: assistant },
  { sender: MessageSender.User, content: '', toolContent: user }
]

type GQLSender = Exclude<ChatMessageSender, 'step'>

/**
 * Maps the local chat sender vocabulary onto the GraphQL `MessageSender`
 * enum. `step` messages are excluded upstream via the `GQLSender` type — the
 * exhaustive switch makes adding a new chat sender a TypeScript error until
 * the mapping is updated.
 */
function toGQLSender(sender: GQLSender): MessageSender {
  switch (sender) {
    case 'user':
      return MessageSender.User
    case 'assistant':
      return MessageSender.Assistant
    default: {
      const exhaustive: never = sender
      throw new Error(`Unhandled sender: ${exhaustive}`)
    }
  }
}

/**
 * Converts user/assistant messages into the subscription's HistoryEntry[] shape
 * for the backend. Drops step messages and caps to the most recent
 * MAX_HISTORY_MESSAGES entries so the LLM context stays bounded.
 *
 * A slash pick sent with no typed text carries the whole request in the chip,
 * so the chip becomes the content. Left empty, the turn says nothing, the
 * server drops it, and the advisor's request is missing from every turn that
 * follows.
 */
export function buildHistory(messages: ChatMessage[]): HistoryEntry[] {
  return messages
    .filter((m): m is ChatMessage & { sender: GQLSender } => m.sender !== 'step')
    .slice(-MAX_HISTORY_MESSAGES)
    .map(({ sender, text, skill }) => ({
      sender: toGQLSender(sender),
      content: text.trim() === '' && skill ? `/${skill}` : text
    }))
}

/**
 * Places the tool turns of the just-completed turn into the text history.
 *
 * They belong immediately before that turn's final assistant message, so the
 * conversation reads "called the tool, read the result, then answered". Until the
 * model answers there is no such message, and they belong at the end, in the
 * order they happened. Dropping them instead costs the model the whole workflow
 * it is in the middle of.
 */
export function withToolTurns(
  textHistory: HistoryEntry[],
  toolTurns: HistoryEntry[]
): HistoryEntry[] {
  const insertAt =
    textHistory[textHistory.length - 1]?.sender === MessageSender.Assistant
      ? textHistory.length - 1
      : textHistory.length
  return [...textHistory.slice(0, insertAt), ...toolTurns, ...textHistory.slice(insertAt)]
}
